import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';

export type FWizardStepStatus = 'pending' | 'active' | 'complete' | 'blocked' | 'error' | 'warning';

export type FWizardStepConfig = {
  code: string;
  title: string;
  subtitle?: string;
  description?: string;
  status?: FWizardStepStatus;
  canEnter?: boolean;
  canContinue?: boolean;
  completion?: number;
  requirement?: string;
  render: () => React.ReactNode;
};

type Props = {
  title: string;
  subtitle?: string;
  steps: FWizardStepConfig[];
  activeStep: string;
  loading?: boolean;
  saving?: boolean;
  error?: string;
  success?: string;
  warning?: string;
  onStepChange: (code: string) => void;
  onFinish?: () => void;
  onSaveDraft?: () => void;
  finishLabel?: string;
};

class FWizard extends React.PureComponent<Props> {
  activeIndex() {
    return Math.max(0, this.props.steps.findIndex((x) => x.code === this.props.activeStep));
  }

  activeStep() {
    return this.props.steps[this.activeIndex()] || this.props.steps[0];
  }

  progress() {
    const steps = this.props.steps || [];
    if (!steps.length) return 0;

    const complete = steps.filter((x) => x.status === 'complete').length;
    const active = steps.some((x) => x.code === this.props.activeStep) ? 0.5 : 0;

    return Math.min(100, ((complete + active) / steps.length) * 100);
  }

  canGoBack() {
    return this.activeIndex() > 0;
  }

  canGoNext() {
    const idx = this.activeIndex();
    const current = this.activeStep();
    return idx < this.props.steps.length - 1 && current?.canContinue !== false;
  }

  previous = () => {
    if (!this.canGoBack()) return;
    const prev = this.props.steps[this.activeIndex() - 1];
    this.props.onStepChange(prev.code);
  };

  next = () => {
    if (!this.canGoNext()) return;
    const next = this.props.steps[this.activeIndex() + 1];
    if (next.canEnter === false) return;
    this.props.onStepChange(next.code);
  };

  stepColor(status?: FWizardStepStatus): any {
    if (status === 'complete') return 'success.main';
    if (status === 'active') return 'primary.main';
    if (status === 'blocked') return 'text.disabled';
    if (status === 'error') return 'error.main';
    if (status === 'warning') return 'warning.main';
    return 'text.secondary';
  }

  renderStepper() {
    const active = this.props.activeStep;

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Wizard Progress</Typography>
            <Typography variant="caption" color="text.secondary">{Math.round(this.progress())}% complete</Typography>
          </Stack>

          <LinearProgress variant="determinate" value={this.progress()} sx={{ height: 7, borderRadius: 5 }} />

          <Stack spacing={1}>
            {this.props.steps.map((step, index) => {
              const status: FWizardStepStatus = step.code === active ? 'active' : (step.status || 'pending');
              const disabled = step.canEnter === false;

              return (
                <Card
                  key={step.code}
                  variant={step.code === active ? 'outlined' : 'elevation'}
                  elevation={step.code === active ? 0 : 1}
                  onClick={() => !disabled && this.props.onStepChange(step.code)}
                  sx={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.55 : 1,
                    borderColor: step.code === active ? 'primary.main' : undefined,
                  }}
                >
                  <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: step.code === active ? 'primary.main' : status === 'complete' ? 'success.main' : '#f0f0f0',
                          color: step.code === active || status === 'complete' ? '#fff' : 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 800,
                          flex: '0 0 auto',
                        }}
                      >
                        {status === 'complete' ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : index + 1}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: this.stepColor(status) }}>
                          {step.title}
                        </Typography>
                        {step.subtitle && (
                          <Typography variant="caption" color="text.secondary">
                            {step.subtitle}
                          </Typography>
                        )}
                        {step.requirement && status !== 'complete' && (
                          <Typography variant="caption" color="warning.main" display="block">
                            {step.requirement}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  renderAlerts() {
    return (
      <>
        {this.props.error && <Alert severity="error">{this.props.error}</Alert>}
        {this.props.warning && <Alert severity="warning">{this.props.warning}</Alert>}
        {this.props.success && <Alert severity="success">{this.props.success}</Alert>}
      </>
    );
  }

  renderNavigation() {
    const isLast = this.activeIndex() === this.props.steps.length - 1;

    return (
      <Paper elevation={1} sx={{ p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={this.previous}
            disabled={!this.canGoBack() || this.props.loading || this.props.saving}
          >
            Back
          </Button>

          <Stack direction="row" spacing={1}>
            {this.props.onSaveDraft && (
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={this.props.onSaveDraft}
                disabled={this.props.loading || this.props.saving}
              >
                Save Draft
              </Button>
            )}

            {isLast ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={this.props.onFinish}
                disabled={!this.activeStep()?.canContinue || this.props.loading || this.props.saving}
              >
                {this.props.finishLabel || 'Finish'}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={this.next}
                disabled={!this.canGoNext() || this.props.loading || this.props.saving}
              >
                Continue
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  render() {
    const active = this.activeStep();

    return (
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{this.props.title}</Typography>
          {this.props.subtitle && (
            <Typography variant="body2" color="text.secondary">{this.props.subtitle}</Typography>
          )}
        </Paper>

        {this.renderAlerts()}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '320px 1fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box>{this.renderStepper()}</Box>

          <Stack spacing={2}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{active?.title}</Typography>
              {active?.description && (
                <Typography variant="body2" color="text.secondary">{active.description}</Typography>
              )}
              <Divider sx={{ my: 2 }} />
              {active?.render?.()}
            </Paper>

            {this.renderNavigation()}
          </Stack>
        </Box>
      </Stack>
    );
  }
}

export default FWizard;
